import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  CircularProgress,
  MenuItem,
  // TextareaAutosize, // Removed unused import
} from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles"; // Import useTheme

// --- Custom Adapter (Removed as TextareaAutosize is not used) ---
// const TextareaAutosizeAdapter = React.forwardRef(
//   function TextareaAutosizeAdapter(props, ref) {
//     const { inputRef, ...other } = props;
//     // eslint-disable-next-line react/prop-types
//     return <TextareaAutosize ref={inputRef || ref} {...other} />;
//   }
// );

const useStyles = makeStyles((theme) => ({
  parseDialogTextArea: {
    width: "100%",
    marginTop: theme.spacing(2), // Keep margin for the text area
  },
  parseDialogField: {
    // Fields in the review section will get spacing via Grid container
  },
  parseDialogSection: {
    // Not strictly needed if Grid handles spacing
  },
  parseDialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2), // Add space below header in review stage
  },
  dialogContent: {
    paddingTop: theme.spacing(1), // Add a bit of top padding
  },
  dialogActions: {
    padding: theme.spacing(2, 3), // Standard padding for actions
  },
}));

const JobParseDialog = ({ open, onClose, onConfirm }) => {
  const classes = useStyles();
  const theme = useTheme(); // Get theme for spacing
  const [parseDescription, setParseDescription] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null); // Stores raw API response
  const [editParsedData, setEditParsedData] = useState({
    // Stores editable data
    jobTitle: "",
    companyName: "",
    jobLocation: "",
    jobType: "",
    salary: "",
    applicationDeadline: "",
    jobDescription: "",
  });
  const [parseViewStage, setParseViewStage] = useState("input"); // 'input' or 'review'
  const [alertMessage, setAlertMessage] = useState(null); // For errors within the dialog

  // Reset state when dialog closes or opens
  useEffect(() => {
    if (open) {
      setParseDescription("");
      setParsedData(null);
      setEditParsedData({
        jobTitle: "",
        companyName: "",
        jobLocation: "",
        jobType: "",
        salary: "",
        applicationDeadline: "",
        jobDescription: "",
      });
      setParseViewStage("input");
      setAlertMessage(null);
      setParsing(false);
    }
  }, [open]);

  /* Call GCF to parse job description text */
  const handleParseJobDescription = async () => {
    if (!parseDescription.trim()) {
      setAlertMessage("Please enter a job description to parse.");
      return;
    }
    setAlertMessage(null); // Clear previous alerts
    setParsing(true);
    setParsedData(null); // Clear previous parsed data before new request
    try {
      // Replace with your actual Cloud Function URL
      const response = await fetch(
        "https://us-central1-resumerevivalapp.cloudfunctions.net/process_job_description",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jobDescription: parseDescription }),
        }
      );
      if (!response.ok) {
        const errorBody = await response.text(); // Try to get more error details
        console.error("Parsing error response:", errorBody);
        throw new Error(
          `Failed to parse job description (Status: ${response.status})`
        );
      }
      const data = await response.json();
      setParsedData(data); // Store the raw response
      // Initialize editable state with parsed data or fallbacks
      setEditParsedData({
        jobTitle: data.jobTitle || "",
        companyName: data.companyName || "",
        jobLocation: data.jobLocation || "",
        jobType: data.jobType || "",
        salary: data.salary || "",
        applicationDeadline: data.applicationDeadline || "",
        // Use original parseDescription if API doesn't return one, or the API's version if it does
        jobDescription: data.jobDescription || parseDescription,
      });
      setParseViewStage("review");
    } catch (error) {
      console.error("Parsing Error:", error);
      setAlertMessage(`Failed to parse: ${error.message}. Please try again.`);
    } finally {
      setParsing(false);
    }
  };

  /* Edit parse dialog fields */
  const handleEditParsedField = (fieldName, value) => {
    setEditParsedData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  /* Confirm parse data => calls onConfirm prop */
  const handleConfirmParsedData = () => {
    onConfirm(editParsedData); // Pass the potentially edited data back
    onClose(); // Close the dialog
  };

  /* Return to "input" stage in parse dialog */
  const handleBackToInput = () => {
    setParseViewStage("input");
    setParsedData(null); // Clear parsed data when going back
    // Keep parseDescription so user doesn't lose input
    setEditParsedData({
      // Reset edit fields
      jobTitle: "",
      companyName: "",
      jobLocation: "",
      jobType: "",
      salary: "",
      applicationDeadline: "",
      jobDescription: "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Parse Job Description</DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {alertMessage && (
          <Box mb={2}>
            <Alert severity="error" onClose={() => setAlertMessage(null)}>
              {alertMessage}
            </Alert>
          </Box>
        )}

        {parseViewStage === "input" && (
          <>
            <Typography variant="body1" gutterBottom>
              Paste the job description below:
            </Typography>
            <TextField
              // className={classes.parseDialogTextArea} // Use margin directly below
              style={{ marginTop: theme.spacing(1) }} // Add specific margin
              label="Job Description Input"
              variant="outlined"
              multiline
              minRows={15} // Slightly reduced rows for dialog context
              maxRows={30}
              fullWidth
              value={parseDescription}
              onChange={(e) => setParseDescription(e.target.value)}
            />
            <Box marginTop={2.5} display="flex" alignItems="center">
              <Button
                variant="contained" // Keep contained for primary action
                color="primary"
                onClick={handleParseJobDescription}
                disabled={parsing || !parseDescription.trim()}
              >
                {parsing ? "Parsing..." : "Parse"}
              </Button>
              {parsing && (
                <Box ml={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </>
        )}

        {parseViewStage === "review" && (
          <>
            <Box className={classes.parseDialogHeader}>
              <Typography variant="h6">Review Parsed Details</Typography>
              <Button
                color="default"
                onClick={handleBackToInput}
                variant="outlined" // Outline for secondary action
              >
                Go Back
              </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Review the extracted information below and make any necessary
              corrections before confirming.
            </Typography>
            <Grid container spacing={3} style={{ marginTop: theme.spacing(1) }}>
              {/* Show Skeletons while parsing is technically done but state might not be updated yet */}
              {parsing || !parsedData ? (
                <>
                  {[...Array(6)].map((_, i) => (
                    <Grid item xs={12} sm={6} key={`skel-field-${i}`}>
                      <Skeleton variant="rect" height={56} />
                    </Grid>
                  ))}
                  <Grid item xs={12} key="skel-desc">
                    <Skeleton variant="rect" height={150} />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Job Title"
                      variant="outlined"
                      fullWidth
                      value={editParsedData.jobTitle}
                      onChange={(e) =>
                        handleEditParsedField("jobTitle", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company Name"
                      variant="outlined"
                      fullWidth
                      value={editParsedData.companyName}
                      onChange={(e) =>
                        handleEditParsedField("companyName", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Job Location"
                      variant="outlined"
                      fullWidth
                      value={editParsedData.jobLocation}
                      onChange={(e) =>
                        handleEditParsedField("jobLocation", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Job Type"
                      variant="outlined"
                      select
                      fullWidth
                      value={editParsedData.jobType}
                      onChange={(e) =>
                        handleEditParsedField("jobType", e.target.value)
                      }
                    >
                      <MenuItem value="">Select Job Type</MenuItem>
                      <MenuItem value="Full-time">Full-time</MenuItem>
                      <MenuItem value="Part-time">Part-time</MenuItem>
                      <MenuItem value="Contract">Contract</MenuItem>
                      <MenuItem value="Internship">Internship</MenuItem>
                      <MenuItem value="Temporary">Temporary</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Salary"
                      variant="outlined"
                      fullWidth
                      value={editParsedData.salary}
                      onChange={(e) =>
                        handleEditParsedField("salary", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Application Deadline"
                      variant="outlined"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={editParsedData.applicationDeadline}
                      onChange={(e) =>
                        handleEditParsedField(
                          "applicationDeadline",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Job Description"
                      variant="outlined"
                      multiline
                      minRows={10} // Reduced minRows for dialog view
                      maxRows={20}
                      fullWidth
                      value={editParsedData.jobDescription}
                      onChange={(e) =>
                        handleEditParsedField("jobDescription", e.target.value)
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        {parseViewStage === "input" && (
          <Button onClick={onClose} color="default">
            Cancel
          </Button>
        )}
        {parseViewStage === "review" && (
          <>
            <Button onClick={onClose} color="default">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmParsedData}
              color="primary"
              variant="contained"
              disabled={!parsedData} // Disable confirm if parsing failed or hasn't run
            >
              Confirm and Inject
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

JobParseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default JobParseDialog;
